import type {ReactElement} from 'react';
import {useContext} from 'react';
import {WIZARD_HEADER_CLASS} from '@Libraries/social-media-wizard-library';
import {
  BLURABLE_CLASS_IN_AI_CAPTION_MODE,
  SocialMediaWizardContentSize,
  SocialMediaWizardStep,
  WizardMobileNavView,
} from '@Components/social-media-wizard/social-media-wizard.types';
import {useHaveAllPostsPublished, useSocialMediaWizardPost, useWizardStep} from '@Components/social-media/social-media.hooks';
import {SocialMediaWizardContainerQueriesContext} from '@Components/social-media-wizard/social-media-wizard-container-queries-context';
import {useWizardMobileNavView} from '@Components/social-media-wizard/social-media-wizard.hooks';
import {isUserLoggedIn, isUserPremium} from '@Libraries/user.library';
import styles from './social-media-wizard-content.module.scss';
import {ShareSocialPostInTeamToggler} from '@Components/share-social-post-in-team-toggler';
import {ShareInTeamBarState} from '@Components/share-in-team-toggle-bar/share-in-team-toggle-bar.types';
import {GA4EventName} from '@Libraries/ga-events';

interface SocialMediaWizardContentProps {
  heading: string;
  mainContent: ReactElement<any>;
  fluidWidth?: boolean;
  previewContent?: ReactElement<any>;
  size?: SocialMediaWizardContentSize;
}

export function SocialMediaWizardContent({
  heading,
  mainContent,
  fluidWidth = false,
  previewContent = undefined,
  size = SocialMediaWizardContentSize.XLARGE,
}: SocialMediaWizardContentProps): ReactElement<any> {
  const isWeb = size === SocialMediaWizardContentSize.XLARGE || size === SocialMediaWizardContentSize.LARGE || size === SocialMediaWizardContentSize.MEDIUM;
  const wizardStep = useWizardStep();
  const post = useSocialMediaWizardPost();
  const haveAllPostsPublished = useHaveAllPostsPublished();
  const wizardMobileNavView = useWizardMobileNavView();
  const {isSmallScreen, isExtraSmallScreen, } = useContext(SocialMediaWizardContainerQueriesContext);
  const isLoggedIn = isUserLoggedIn();

  /**
   * Whether to use the web (multi-column) layout rather than the mobile (single-column) one. Both layouts
   * render the SAME element tree down to mainContent/previewContent, differing only in classNames — so
   * crossing the breakpoint re-renders in place instead of unmounting and remounting the content subtree.
   * A remount would re-run the children's mount effects (e.g. PublishingPost's publishing-status init),
   * which is what reset an already-published post back to "Publishing" on resize.
   */
  const isWebLayout = (): boolean => {
    return isWeb || wizardStep === SocialMediaWizardStep.SELECT_DESIGN;
  };

  const getContentContainerStyles = (): string => {
    if (size === SocialMediaWizardContentSize.XSMALL) {
      return `${styles.xsmall} ${isAddDetailsPreview() ? styles.overflowHidden : ''}`;
    }

    if (size === SocialMediaWizardContentSize.SMALL) {
      return `${styles.small} ${isAddDetailsPreview() ? styles.overflowHidden : ''}`;
    }

    if (size === SocialMediaWizardContentSize.MEDIUM) {
      return styles.medium;
    }

    if (size === SocialMediaWizardContentSize.LARGE) {
      return styles.large;
    }

    return styles.xlarge;
  };

  const isAddDetailsPreview = (): boolean => {
    return wizardStep === SocialMediaWizardStep.ADD_DETAILS && wizardMobileNavView === WizardMobileNavView.PREVIEW;
  };

  const getWizardHeaderClasses = (): string => {
    let topSpacingClass = '';
    if (size === SocialMediaWizardContentSize.XSMALL) {
      topSpacingClass = 'spacing-m-t-4';
    }

    const bottomMarginClass = wizardStep !== SocialMediaWizardStep.SELECT_DESIGN ? 'spacing-m-b-5' : 'spacing-m-b-3';

    return `_full-width ct ${topSpacingClass} ${isShareInTeamTogglerShowing() ? 'spacing-m-b-1' : bottomMarginClass} ${BLURABLE_CLASS_IN_AI_CAPTION_MODE}`;
  };

  const getWizardHeading = (): ReactElement<any> | null => {
    return (
      <div className={getWizardHeaderClasses()}>
        <span className={`body-xl-bold-deprecated flex-vertical-center ${WIZARD_HEADER_CLASS} ${styles.wizardContentHeading}`}>{heading}</span>
      </div>
    );
  };

  const getShareInTeamElementIfRequired = (): ReactElement<any> | null => {
    if (!isShareInTeamTogglerShowing() || !post) {
      return null;
    }

    return (
      <div className={`${styles.shareInTeamContainer} spacing-m-b-6`}>
        <ShareSocialPostInTeamToggler post={post} onStateUpdate={onSharedInTeam} />
      </div>
    );
  };

  const onSharedInTeam = (state: ShareInTeamBarState): void => {
    if (state === ShareInTeamBarState.ACTIVE) {
      window.PMW.gtm.trackGA4CustomEvent(GA4EventName.COLLABORATION_SOCIALPOST_SHAREINTEAM_WIZARD);
    }
  };

  const isShareInTeamTogglerShowing = (): boolean => {
    return isUserPremium() && post !== undefined && haveAllPostsPublished;
  };

  const getHeadingSlot = (): ReactElement<any> | null => {
    if (isWebLayout()) {
      return getWizardHeading();
    }

    return <div className={`flex-h-row flex-content-center ${getHeadingMargins()}`}>{getHeadingForMobile()}</div>;
  };

  const isMainContentVisibleOnMobile = (): boolean => {
    return wizardMobileNavView === WizardMobileNavView.CONTENT || !previewContent;
  };

  const getMainContentWrapperClasses = (): string => {
    if (isWebLayout()) {
      return `${!previewContent ? styles.wide : 'spacing-m-r-10'} ${styles.content}`;
    }

    return `_full-width ${isMainContentVisibleOnMobile() ? '' : '_hidden'}`;
  };

  const getPreviewContentWrapperClasses = (): string => {
    if (isWebLayout()) {
      return `${!previewContent ? '_hidden' : ''} ${styles.content}`;
    }

    return `_full-width ${!isMainContentVisibleOnMobile() && previewContent ? '' : '_hidden'}`;
  };

  const getHeadingMargins = (): string => {
    if (isShareInTeamTogglerShowing()) {
      return 'spacing-m-b-2';
    }

    if (wizardMobileNavView === WizardMobileNavView.PREVIEW) {
      return '';
    }

    if (wizardStep === SocialMediaWizardStep.ADD_DETAILS) {
      return `spacing-m-b-8`;
    }

    return `spacing-m-b-4`;
  };

  const getHeadingForMobile = (): ReactElement<any> | null => {
    return wizardMobileNavView === WizardMobileNavView.CONTENT ? (
      <span className={`${isExtraSmallScreen ? `body-m-bold` : `body-xl-bold-deprecated`} flex-vertical-center ${WIZARD_HEADER_CLASS} ${styles.wizardContentHeading}`}>{heading}</span>
    ) : null;
  };

  const getMarginForContentWrapper = (): string => {
    if (!isSmallScreen && !isExtraSmallScreen) {
      return `spacing-m-t-10`;
    }

    if (wizardMobileNavView === WizardMobileNavView.CONTENT) {
      if (size === SocialMediaWizardContentSize.XSMALL && wizardStep === SocialMediaWizardStep.SELECT_DESIGN) {
        return '';
      }

      let classes = ``;
      if (isExtraSmallScreen || isSmallScreen) {
        classes = styles.mobileContentPadding;
      }

      return `${classes} spacing-m-t-6`;
    }

    if (wizardMobileNavView === WizardMobileNavView.PREVIEW) {
      return `spacing-m-t-4`;
    }

    return `spacing-m-t-10`;
  };

  const getContentClasses = (): string => {
    return `
    flex-v-row _full-width flex-center
    ${styles.contentContainer}
    ${getMarginForContentWrapper()}
    ${fluidWidth ? styles.fluid : ''}
    ${isLoggedIn ? '' : '_unpadded-bottom'}
    ${getContentContainerStyles()}
    ${isWebLayout() ? '' : `${styles.mobileWrapper} spacing-p-r-4 spacing-p-l-4 spacing-m-b-5`}
   `;
  };

  return (
    <div className={getContentClasses()}>
      {getHeadingSlot()}
      {getShareInTeamElementIfRequired()}
      <div className={isWebLayout() ? 'flex-h-row _full-width' : '_full-width'}>
        <div className={getMainContentWrapperClasses()}>{mainContent}</div>
        <div className={getPreviewContentWrapperClasses()}>{previewContent}</div>
      </div>
    </div>
  );
}
